import React, { useState, useEffect } from 'react';
import { Box, Tooltip, Avatar, CircularProgress, IconButton } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const apiUrl = process.env.REACT_APP_API_URL || '';

const ProfileScoreCircle = ({ userId, onClick, size = 40, image, ...props }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profileScore, setProfileScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [internalImage, setInternalImage] = useState(null);

  const finalImage = image || internalImage;

  useEffect(() => {
    if (userId) {
      fetchProfileScore();
      
      const handleScoreUpdate = (event) => {
        if (event.detail && event.detail.score !== undefined) {
          setProfileScore(event.detail.score);
        } else {
          // Fallback to refetching if score isn't passed in detail
          fetchProfileScore();
        }
      };

      window.addEventListener('profileScoreUpdated', handleScoreUpdate);
      
      return () => {
        window.removeEventListener('profileScoreUpdated', handleScoreUpdate);
      };
    }
  }, [userId]);

  const fetchProfileScore = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}authentication/get_candidate_profile_score/${userId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      if (response.data && response.data.profile_score !== undefined) {
        setProfileScore(response.data.profile_score);
      }
      
      // Only fetch image if not provided via props
      if (!image) {
        const userResponse = await axios.get(
          `${apiUrl}authentication/user_list/${userId}/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`
            }
          }
        );
        if (userResponse.data && userResponse.data.image) {
          setInternalImage(userResponse.data.image);
        }
      }
    } catch (error) {
      console.error('Error fetching profile score:', error);
      setProfileScore(0);
    } finally {
      setLoading(false);
    }
  };

  const getColor = (score) => {
    if (score <= 25) return '#F44336'; // Red
    if (score <= 50) return '#FF9800'; // Orange
    if (score <= 75) return '#FFD700'; // Gold
    return '#00C853'; // Green
  };

  return (
    <Tooltip
      title={
        <Box sx={{ textAlign: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, marginTop: '4px' }}>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>
              {profileScore}%
            </div>
            <IconButton 
              size="small" 
           
              sx={{ 
                color: 'white',
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
            </IconButton>
          </Box>
        </Box>
      }
      arrow
      placement="bottom"
      TransitionProps={{
        timeout: 300,
      }}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.9)',
            },
            borderRadius: '8px',
            padding: '8px 12px',
          },
        },
      }}
    >
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          width: size,
          height: size,
          cursor: 'pointer',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
        {...props}
      >
        {/* Background Circle */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={3}
          sx={{
            color: 'rgba(255, 255, 255, 0.2)',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />

        {/* Progress Circle */}
        <CircularProgress
          variant="determinate"
          value={profileScore}
          size={size}
          thickness={3}
          sx={{
            color: getColor(profileScore),
            position: 'absolute',
            top: 0,
            left: 0,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
              transition: 'stroke-dashoffset 0.5s ease',
            },
          }}
        />

        {/* Profile Avatar */}
        <Avatar
          src={finalImage}
          sx={{
            width: size - 8,
            height: size - 8,
            position: 'absolute',
            top: 4,
            left: 4,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Fallback to first letter if no image */}
          {!finalImage && props.fallbackText}
        </Avatar>
      </Box>
    </Tooltip>
  );
};

export default ProfileScoreCircle;
